import React, { useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Dialog } from "@headlessui/react";
import { Bars3Icon, XMarkIcon, UserIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, Fragment } from "react";
import { UserContext } from '../../providers/UserProvider';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Header = (props: any) => {
  const { t } = useTranslation();
  const {userBio, handleLogout} = useContext(UserContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  const isTransparent = !scrolled;

  const navigation = [
    { name: t('header.nav.eelj'), href: "/eelj" },
    { name: t('header.nav.news'), href: "/news" },
    { name: t('header.nav.ebook'), href: "/ebook" },
    { name: t('header.nav.program'), href: "/program" },
    { name: t('header.nav.parent'), href: "/parent" },
    { name: t('header.nav.about'), href: "/about" },
    { name: t('header.nav.transparency'), href: "/transparency" },
  ];

  useEffect(() => {
    setMenuOpen(false);
  }, [userBio]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 w-full z-50 h-20 transition-all duration-300 ${
      scrolled
        ? 'bg-white/95 backdrop-blur-md shadow-md'
        : 'bg-primary-800'
    }`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-x-4 h-full px-4 lg:px-8">
        <div className="flex lg:flex-1 items-center h-full">
          <Link to="/" className="flex items-center space-x-2 group h-full">
            <img src="/logo.png" alt={t('common.nairamdal')} height={56} width={68} className="h-12 w-auto" />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex lg:gap-x-1 lg:items-center h-full">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap flex items-center ${
                  isActive
                    ? isTransparent
                      ? 'bg-white/15 text-white'
                      : 'bg-primary-100 text-primary-700'
                    : isTransparent
                      ? 'text-white/80 hover:bg-white/10 hover:text-white'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                }`}
              >
                {item.name}
              </Link>
            );
          })}
          <a
            target="blank"
            href="https://old.nairamdal.mn/360nairamdal/index.html"
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap ${
              isTransparent
                ? 'text-white/80 hover:bg-white/10 hover:text-white'
                : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
            }`}
          >
            360°
          </a>
        </nav>

        {/* Desktop Auth */}
        <div className="hidden lg:flex items-center gap-x-3 h-full">
          <LanguageSwitcher isTransparent={isTransparent} />
          {userBio || localStorage.getItem('token') ?
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`flex items-center justify-center w-10 h-10 rounded-full shadow-md transition-all duration-200 ${
                isTransparent
                  ? 'bg-white/15 text-white hover:bg-white/25 border border-white/20'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              <UserIcon className="h-5 w-5" />
            </button>
            :
            <Link to={'/login'}>
              {isTransparent ? (
                <button className="text-sm px-6 py-2.5 rounded-full font-semibold bg-white text-neutral-900 hover:bg-white/90 transition-colors">
                  {t('header.login')}
                </button>
              ) : (
                <button className="btn-primary text-sm px-6 py-2.5">{t('header.login')}</button>
              )}
            </Link>
          }
        </div>

        {/* Mobile */}
        <div className="flex items-center lg:hidden gap-x-3 h-full">
          <LanguageSwitcher isTransparent={isTransparent} />
          {!localStorage.getItem('token') && (
            <Link to={'/login'}>
              {isTransparent ? (
                <button className="text-xs px-5 py-2 rounded-full font-semibold bg-white text-neutral-900" onClick={() => setMobileMenuOpen(false)}>
                  {t('header.login')}
                </button>
              ) : (
                <button className="btn-primary text-xs px-5 py-2" onClick={() => setMobileMenuOpen(false)}>
                  {t('header.login')}
                </button>
              )}
            </Link>
          )}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`inline-flex items-center justify-center rounded-full p-2 transition-colors ${
              isTransparent
                ? 'text-white hover:bg-white/10'
                : 'text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            <span className="sr-only">{t('header.openMenu')}</span>
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* User Dropdown */}
      <div aria-live="assertive" className="pointer-events-none fixed w-screen inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-50">
        {(userBio || localStorage.getItem('token')) && menuOpen &&
          <div className="flex w-full flex-col items-center space-y-4 sm:items-end pt-20 sm:pt-20 max-w-7xl mx-auto">
            <div className="pointer-events-auto w-full max-w-xs card p-2">
              <Link to="/profile">
                <button type="button" onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-4 py-3 rounded-2xl text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-all font-semibold">
                  {t('header.profile')}
                </button>
              </Link>
              {userBio?.role === 'teacher' && (
                <>
                  <div className="h-px bg-neutral-100 my-1 mx-4"></div>
                  <Link to="/grouplist">
                    <button type="button" onClick={() => setMenuOpen(false)}
                      className="w-full text-left px-4 py-3 rounded-2xl text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-all font-semibold">
                      {t('header.groupList')}
                    </button>
                  </Link>
                </>
              )}
              <div className="h-px bg-neutral-100 my-1 mx-4"></div>
              <button type="button" onClick={() => { handleLogout(); nav('/'); setMenuOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-2xl text-danger-600 hover:bg-danger-50 transition-all font-semibold">
                {t('header.logout')}
              </button>
            </div>
          </div>
        }
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm shadow-2xl animate-slide-in-right">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <img src="/nairamdal_logo_new.png" height={50} width={130} alt={t('common.nairamdal')} />
              </Link>
              <button type="button" className="rounded-full p-2 text-neutral-600 hover:bg-neutral-100" onClick={() => setMobileMenuOpen(false)}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-1">
              {userBio ? (
                <div className="mb-6 pb-6 border-b border-neutral-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white">
                      <UserIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">
                        {userBio.firstName && userBio.lastName ? `${userBio.firstName} ${userBio.lastName}` : t('header.user')}
                      </p>
                      <p className="text-sm text-neutral-500">{userBio.role === 'teacher' ? t('header.teacher') : t('header.parentRole')}</p>
                    </div>
                  </div>
                  <Link to="/profile">
                    <button type="button" onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-left px-4 py-3 rounded-2xl text-neutral-700 hover:bg-primary-50 font-semibold">
                      {t('header.profile')}
                    </button>
                  </Link>
                  {userBio?.role === 'teacher' && (
                    <Link to="/grouplist">
                      <button type="button" onClick={() => setMobileMenuOpen(false)}
                        className="w-full text-left px-4 py-3 rounded-2xl text-neutral-700 hover:bg-primary-50 font-semibold">
                        {t('header.groupList')}
                      </button>
                    </Link>
                  )}
                  <button type="button" onClick={() => { setMobileMenuOpen(false); handleLogout(); nav('/'); }}
                    className="w-full text-left px-4 py-3 rounded-2xl text-danger-600 hover:bg-danger-50 font-semibold">
                    {t('header.logout')}
                  </button>
                </div>
              ) : (
                <Link to="/login">
                  <button className="w-full btn-primary mb-6" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.login')}
                  </button>
                </Link>
              )}

              {navigation.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                return (
                  <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-2xl text-base font-semibold transition-all ${
                      isActive ? 'text-primary-700 bg-primary-50' : 'text-neutral-700 hover:bg-neutral-50'
                    }`}>
                    {item.name}
                  </Link>
                );
              })}
              <a target="blank" href="https://old.nairamdal.mn/360nairamdal/index.html"
                className="block px-4 py-3 rounded-2xl text-base font-semibold text-neutral-700 hover:bg-neutral-50">
                360°
              </a>
            </div>
          </Dialog.Panel>
        </Dialog>
      )}
    </header>
  );
};

export default Header;
